import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Plus, Loader2, KeyRound, RefreshCw, Copy, Trash2, Receipt, ChevronRight, ChevronLeft, X } from "lucide-react";
import { api, errMsg, faDigits, faNum, fmtJalaliDate, fmtJalaliDateTime, timeAgo } from "../lib/api";
import { SERVICE_LABEL, SOURCE_SERVICE, STATUS_LABEL } from "../lib/portalLabels";

const card = "bg-white border border-slate-200/80 rounded-2xl shadow-sm p-5";
const input = "w-full border rounded-xl px-3 py-2 text-sm bg-white";
const label = "text-xs text-slate-600 mb-1 block";

const emptyForm = { mobile: "", name: "", max_tokens: 2, nid: 0, shahkar: 0, sana: 0, note: "", is_active: true };

export default function AdminUsers() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold">کاربران و توکن‌ها</h1>
        <p className="text-sm text-slate-500 mt-1">ثبت‌نام عمومی وجود ندارد؛ کاربر را اینجا اضافه کنید. ورود کاربران فقط با کد پیامکی است.</p>
      </div>
      <MasterTokenCard />
      <AddUserCard />
      <UsersList />
    </div>
  );
}

// ---------------- توکن اصلی (فقط مدیر) ----------------
function MasterTokenCard() {
  const [info, setInfo] = useState(null);
  const [fresh, setFresh] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/master-token").then(({ data }) => setInfo(data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const rotate = async () => {
    if (!window.confirm("توکن اصلی تعویض شود؟ توکن فعلی بلافاصله از کار می‌افتد.")) return;
    try {
      const { data } = await api.post("/admin/master-token/rotate");
      setFresh(data.token);
      toast.success("توکن اصلی جدید ساخته شد");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("کپی شد"); }
    catch { toast.error("کپی انجام نشد"); }
  };

  const item = info?.item;
  return (
    <div className={card}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-medium flex items-center gap-2"><KeyRound size={18} className="text-teal-700" /> توکن اصلی (مختص مدیر)</div>
          <div className="text-xs text-slate-500 mt-1">
            {item ? <>پیشوند: <span className="font-mono" dir="ltr">{item.prefix}</span> · فراخوانی کل: {faNum(item.total_calls)} · آخرین استفاده: {item.last_used_at ? timeAgo(item.last_used_at) : "هرگز"}</> : "توکن اصلی تنظیم نشده است"}
          </div>
        </div>
        <button onClick={rotate} className="flex items-center gap-2 text-sm border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 px-3 py-2 rounded-xl">
          <RefreshCw size={14} /> تعویض توکن اصلی
        </button>
      </div>
      {fresh && (
        <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4">
          <div className="text-sm font-medium text-amber-800 mb-2">توکن جدید (فقط همین یک بار نمایش داده می‌شود)</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all text-xs bg-white border rounded-lg p-2" dir="ltr">{fresh}</code>
            <button onClick={() => copy(fresh)} className="p-2 rounded-lg bg-white border"><Copy size={16} /></button>
            <button onClick={() => setFresh(null)} className="p-2 rounded-lg bg-white border"><X size={16} /></button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- افزودن کاربر ----------------
function AddUserCard() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", {
        mobile: form.mobile, name: form.name, max_tokens: Number(form.max_tokens) || 0,
        prices: { nid: Number(form.nid) || 0, shahkar: Number(form.shahkar) || 0, sana: Number(form.sana) || 0 },
        note: form.note, is_active: form.is_active,
      });
      toast.success("کاربر اضافه شد");
      setForm(emptyForm);
      setOpen(false);
      window.dispatchEvent(new Event("users-changed"));
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className={card}>
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 font-medium text-teal-700">
        <Plus size={18} /> افزودن کاربر جدید
      </button>
      {open && (
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div><label className={label}>شماره موبایل *</label><input dir="ltr" className={input} value={form.mobile} onChange={set("mobile")} placeholder="09123456789" required /></div>
          <div><label className={label}>نام</label><input className={input} value={form.name} onChange={set("name")} /></div>
          <div><label className={label}>سقف تعداد توکن فعال</label><input type="number" min={0} className={input} value={form.max_tokens} onChange={set("max_tokens")} /></div>
          <div><label className={label}>قیمت هر استعلام ثبت احوال (ریال)</label><input type="number" min={0} className={input} value={form.nid} onChange={set("nid")} /></div>
          <div><label className={label}>قیمت هر استعلام شاهکار (ریال)</label><input type="number" min={0} className={input} value={form.shahkar} onChange={set("shahkar")} /></div>
          <div><label className={label}>قیمت هر استعلام سنا (ریال)</label><input type="number" min={0} className={input} value={form.sana} onChange={set("sana")} /></div>
          <div className="md:col-span-2"><label className={label}>یادداشت</label><input className={input} value={form.note} onChange={set("note")} /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={set("is_active")} /> حساب فعال باشد</label>
          <div className="md:col-span-3 flex justify-end">
            <button disabled={busy} className="bg-teal-600 hover:bg-teal-700 text-white text-sm px-5 py-2 rounded-xl flex items-center gap-2">
              {busy && <Loader2 size={14} className="animate-spin" />} ذخیره کاربر
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ---------------- لیست و جزئیات کاربران ----------------
function UsersList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const pageSize = 20;

  const load = useCallback(() => {
    api.get("/admin/users", { params: { search: query, page, page_size: pageSize } })
      .then(({ data }) => { setItems(data.items); setTotal(data.total); })
      .catch((e) => toast.error(errMsg(e)));
  }, [query, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("users-changed", h);
    return () => window.removeEventListener("users-changed", h);
  }, [load]);

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      <div className={card}>
        <form onSubmit={(e) => { e.preventDefault(); setPage(1); setQuery(search.trim()); }} className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute right-3 top-2.5 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="جستجوی موبایل یا نام" className="w-full border rounded-xl pr-9 pl-3 py-2 text-sm" />
          </div>
          <button className="bg-slate-900 text-white text-sm px-4 rounded-xl">جستجو</button>
        </form>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-500 border-b">
                <th className="py-2 text-right font-medium">موبایل</th>
                <th className="py-2 text-right font-medium">نام</th>
                <th className="py-2 text-right font-medium">وضعیت</th>
                <th className="py-2 text-right font-medium">سقف توکن</th>
                <th className="py-2 text-right font-medium">تاریخ ثبت</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">کاربری یافت نشد</td></tr>}
              {items.map((u) => (
                <tr key={u.id} onClick={() => setSelected(u.id)} className={`border-b border-slate-100 cursor-pointer hover:bg-teal-50/40 ${selected === u.id ? "bg-teal-50" : ""}`}>
                  <td className="py-2 font-mono" dir="ltr">{faDigits(u.mobile)}</td>
                  <td className="py-2">{u.name || "—"} {u.role === "admin" && <span className="text-[11px] bg-slate-900 text-white rounded px-1.5 py-0.5 mr-1">مدیر</span>}</td>
                  <td className="py-2">{u.is_active ? <span className="text-emerald-700">فعال</span> : <span className="text-rose-600">غیرفعال</span>}</td>
                  <td className="py-2">{faDigits(u.max_tokens)}</td>
                  <td className="py-2 text-slate-500">{fmtJalaliDate(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-4 text-sm">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="flex items-center gap-1 disabled:text-slate-300"><ChevronRight size={16} /> قبلی</button>
          <span className="text-slate-500">صفحه {faDigits(page)} از {faDigits(pages)} · {faNum(total)} کاربر</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="flex items-center gap-1 disabled:text-slate-300">بعدی <ChevronLeft size={16} /></button>
        </div>
      </div>
      {selected && <UserDetail key={selected} userId={selected} onChanged={load} />}
    </div>
  );
}

// ---------------- جزئیات یک کاربر ----------------
function UserDetail({ userId, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);
  const [newName, setNewName] = useState("");
  const [created, setCreated] = useState(null);
  const [requests, setRequests] = useState([]);
  const nav = useNavigate();

  const load = useCallback(() => {
    api.get(`/admin/users/${userId}`).then(({ data }) => {
      setDetail(data);
      const u = data.user;
      setForm({ name: u.name, max_tokens: u.max_tokens, nid: u.prices.nid || 0, shahkar: u.prices.shahkar || 0,
                sana: u.prices.sana || 0, note: u.note, is_active: u.is_active });
    }).catch((e) => toast.error(errMsg(e)));
    api.get(`/admin/users/${userId}/requests`, { params: { page: 1, page_size: 10 } })
      .then(({ data }) => setRequests(data.items)).catch(() => {});
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  if (!detail || !form) {
    return <div className={card + " grid place-items-center py-10"}><Loader2 className="animate-spin text-slate-400" /></div>;
  }
  const u = detail.user;
  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/users/${userId}`, {
        name: form.name, max_tokens: Number(form.max_tokens) || 0,
        prices: { nid: Number(form.nid) || 0, shahkar: Number(form.shahkar) || 0, sana: Number(form.sana) || 0 },
        note: form.note, is_active: form.is_active,
      });
      toast.success("اطلاعات ذخیره شد");
      load();
      onChanged();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const issueToken = async () => {
    try {
      const { data } = await api.post(`/admin/users/${userId}/tokens`, { name: newName });
      setCreated(data.token);
      setNewName("");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const revoke = async (t) => {
    if (!window.confirm(`توکن «${t.name}» باطل شود؟`)) return;
    try { await api.delete(`/admin/tokens/${t.id}`); toast.success("توکن باطل شد"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("کپی شد"); }
    catch { toast.error("کپی انجام نشد"); }
  };

  return (
    <div className="space-y-5">
      <div className={card}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <div className="font-bold" dir="ltr">{faDigits(u.mobile)} <span className="text-slate-500 font-normal" dir="rtl">· {u.name || "بدون نام"}</span></div>
            <div className="text-xs text-slate-500 mt-1">
              فراخوانی کل: {faNum(detail.usage.total)} · پاسخ قطعی: {faNum(detail.usage.answered)}
            </div>
          </div>
          <button onClick={() => nav("/invoices", { state: { userId } })} className="flex items-center gap-2 text-sm border px-3 py-2 rounded-xl hover:bg-slate-50">
            <Receipt size={14} /> صدور پیش‌فاکتور
          </button>
        </div>
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><label className={label}>نام</label><input className={input} value={form.name} onChange={setF("name")} /></div>
          <div><label className={label}>سقف توکن فعال</label><input type="number" min={0} className={input} value={form.max_tokens} onChange={setF("max_tokens")} /></div>
          <div><label className={label}>یادداشت</label><input className={input} value={form.note} onChange={setF("note")} /></div>
          <div><label className={label}>قیمت ثبت احوال (ریال)</label><input type="number" min={0} className={input} value={form.nid} onChange={setF("nid")} /></div>
          <div><label className={label}>قیمت شاهکار (ریال)</label><input type="number" min={0} className={input} value={form.shahkar} onChange={setF("shahkar")} /></div>
          <div><label className={label}>قیمت سنا (ریال)</label><input type="number" min={0} className={input} value={form.sana} onChange={setF("sana")} /></div>
          <div className="md:col-span-3 flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={setF("is_active")} disabled={u.role === "admin"} /> حساب فعال</label>
            <button className="bg-teal-600 hover:bg-teal-700 text-white text-sm px-5 py-2 rounded-xl">ذخیره تغییرات</button>
          </div>
        </form>
      </div>

      <div className={card}>
        <div className="font-medium mb-3">توکن‌های API</div>
        <div className="flex gap-2 mb-4 flex-wrap">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="نام توکن" className="flex-1 min-w-[180px] border rounded-xl px-3 py-2 text-sm" maxLength={100} />
          <button onClick={issueToken} className="bg-slate-900 text-white text-sm px-4 py-2 rounded-xl">صدور توکن</button>
        </div>
        {created && (
          <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3">
            <div className="text-xs text-amber-800 mb-2">این توکن فقط همین یک بار نمایش داده می‌شود:</div>
            <div className="flex gap-2 items-center">
              <code className="flex-1 break-all text-xs bg-white border rounded p-2" dir="ltr">{created}</code>
              <button onClick={() => copy(created)} className="p-2 border rounded bg-white"><Copy size={14} /></button>
              <button onClick={() => setCreated(null)} className="p-2 border rounded bg-white"><X size={14} /></button>
            </div>
          </div>
        )}
        <div className="divide-y divide-slate-100 text-sm">
          {detail.tokens.length === 0 && <div className="py-4 text-center text-slate-400">توکنی وجود ندارد</div>}
          {detail.tokens.map((t) => (
            <div key={t.id} className="py-2.5 flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-medium">{t.name} {t.is_master && <span className="text-[11px] bg-amber-100 text-amber-800 rounded px-1.5">اصلی</span>} {t.revoked && <span className="text-xs text-rose-600">(باطل)</span>}</div>
                <div className="text-xs text-slate-500 font-mono" dir="ltr">{t.prefix}</div>
              </div>
              <div className="text-xs text-slate-500">فراخوانی: {faNum(t.total_calls)} · آخرین: {t.last_used_at ? timeAgo(t.last_used_at) : "—"}</div>
              {!t.revoked && !t.is_master && (
                <button onClick={() => revoke(t)} className="text-rose-600 text-xs flex items-center gap-1 hover:bg-rose-50 px-2 py-1 rounded"><Trash2 size={13} /> ابطال</button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className={card}>
        <div className="font-medium mb-3">آخرین درخواست‌ها</div>
        <div className="divide-y divide-slate-100 text-sm">
          {requests.length === 0 && <div className="py-4 text-center text-slate-400">درخواستی ثبت نشده است</div>}
          {requests.map((r, i) => (
            <div key={`${r.ts}-${i}`} className="py-2 flex justify-between gap-2 flex-wrap">
              <span>{fmtJalaliDateTime(r.ts)}</span>
              <span className="font-mono" dir="ltr">{r.national_code_masked}</span>
              <span>{SERVICE_LABEL[SOURCE_SERVICE[r.source]] || "—"}</span>
              <span>{STATUS_LABEL[r.status] || "ناموفق / خطا"}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
